// @ts-check
/**
 * Sdílený fokus (ui-checklist body 5–7, R-17 analýzy 1C, R-20 sekce 2) — jedna knihovna pro
 * celý web, ne per modul.
 *
 * `autofocus(scope)`: šablona označí první pole formuláře atributem `data-autofocus` (HTML
 * `autofocus` se nepoužívá: platí jen při načtení dokumentu a při dynamickém vložení je
 * nespolehlivý). Zavaděč `app.js` zavolá `autofocus(scope)` až po doběhnutí modulů; bez
 * `requestAnimationFrame`, protože prvek je při načtení už v dokumentu. Skrytý prvek přeskočí
 * (po 303 záložky: přepínač řádku, jinak akce oznámení — OP 1.3.1 R-3).
 *
 * `trapFocus(root, signal, initial)`: past fokusu nemodální vrstvy (zásuvka navigace; modální
 * dialog dostane past od `<dialog>.showModal()`). Po `requestAnimationFrame` přenese fokus na
 * `initial`, jinak na první `[data-autofocus]` v `root`, jinak na první fokusovatelný prvek.
 * Posluchač Tab žije na DOKUMENTU (capture): fokus uvnitř `root` cykluje mezi prvním
 * a posledním prvkem; fokus mimo `root` (klik do nefokusovatelné části vrstvy dá fokus `body`)
 * vrátí Tab na první a Shift+Tab na poslední prvek vrstvy. Konec pasti dává `signal`.
 *
 * `returnFocus(trigger, layer)`: po zavření vrstvy vrátí fokus spouštěči (checklist bod 6),
 * drží-li ho nikdo, `body` nebo prvek uvnitř zavřené vrstvy; fokus jinde zůstává.
 *
 * Žádné texty UI, žádné `data-*` kromě `data-autofocus`, žádný globál: dokument se bere ze `scope`
 * (`scope.ownerDocument ?? scope`), takže funkce běží i v jsdom bez `document` a `window`.
 */

/** Prvky, které přijmou Tab (bez scrimu `tabindex="-1"` a bez vypnutých polí). */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Přenese fokus na první VIDITELNÝ `[data-autofocus]` ve `scope`, jen drží-li fokus dokumentu
 * `body` (nebo nic) — kdo už píše, o pozici nepřijde. Skrytý prvek (`checkVisibility()` = false;
 * přepínač řádku ve sloupci akcí skrytém pod `sm:`) se přeskočí a vyhraje další v pořadí DOM
 * (akce oznámení; OP 1.3.1 R-3). Prostředí bez `checkVisibility` (jsdom) bere prvek jako
 * viditelný. Bez viditelného prvku nic nedělá a nevyhazuje.
 * @param {ParentNode} scope dokument nebo prvek, v němž se pole hledá
 * @returns {void}
 */
export function autofocus(scope) {
  const doc = scope.ownerDocument ?? /** @type {Document} */ (scope);
  const active = doc.activeElement;
  if (active !== null && active !== doc.body) return;
  const all = /** @type {HTMLElement[]} */ (Array.from(scope.querySelectorAll('[data-autofocus]')));
  all.find(visible)?.focus();
}

/**
 * Je prvek vykreslený (ne pod `display: none` předka)? Bez `checkVisibility` ano.
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function visible(el) {
  return typeof el.checkVisibility !== 'function' || el.checkVisibility();
}

/**
 * Fokusovatelné prvky `root` v pořadí dokumentu, bez prvků pod předkem s atributem `hidden`.
 * @param {HTMLElement} root
 * @returns {HTMLElement[]}
 */
export function focusables(root) {
  const all = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll(FOCUSABLE)));
  // tabindex="-1" (scrim) vyřadí i prvek, který selektor pustil jako button.
  return all.filter(
    (el) => el.getAttribute('tabindex') !== '-1' && el.closest('[hidden]') === null,
  );
}

/**
 * Past fokusu uvnitř `root` do zrušení `signal`: počáteční fokus po `requestAnimationFrame`
 * (prvek je právě vložen nebo zobrazen), pak Tab / Shift+Tab mezi prvním a posledním
 * fokusovatelným prvkem; fokus mimo `root` vede Tab dovnitř. Prázdný `root` nic nedělá.
 * @param {HTMLElement} root vrstva (zásuvka), v níž má fokus zůstat
 * @param {AbortSignal} signal zrušení pasti (zavření vrstvy)
 * @param {HTMLElement | null} [initial] prvek pro počáteční fokus; bez něj `[data-autofocus]`,
 *   jinak první fokusovatelný
 * @returns {void}
 */
export function trapFocus(root, signal, initial = null) {
  const doc = root.ownerDocument;
  requestAnimationFrame(() => {
    if (signal.aborted) return;
    const marked = /** @type {HTMLElement | null} */ (root.querySelector('[data-autofocus]'));
    const target = initial ?? marked ?? focusables(root)[0];
    target?.focus();
  });
  doc.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Tab') return;
      const list = focusables(root);
      if (list.length === 0) return;
      const first = list[0];
      const last = list[list.length - 1];
      const active = doc.activeElement;
      const inside = active !== null && root.contains(active);
      if (!inside) {
        (event.shiftKey ? last : first).focus();
        event.preventDefault();
      } else if (event.shiftKey && active === first) {
        last.focus();
        event.preventDefault();
      } else if (!event.shiftKey && active === last) {
        first.focus();
        event.preventDefault();
      }
    },
    { capture: true, signal },
  );
}

/**
 * Vrátí fokus spouštěči zavřené vrstvy, drží-li ho nikdo, `body` nebo prvek uvnitř `layer`
 * (zavřená vrstva ho ztratí, prohlížeč po kliku na scrim nic nevrací); fokus jinde zůstává.
 * @param {HTMLElement} trigger spouštěč vrstvy (tlačítko „Otevřít menu")
 * @param {HTMLElement} layer zavřená vrstva
 * @returns {void}
 */
export function returnFocus(trigger, layer) {
  const doc = layer.ownerDocument;
  const active = doc.activeElement;
  if (active === null || active === doc.body || layer.contains(active)) trigger.focus();
}
